// ===== SCROLL STORE =====

import { create } from 'zustand';

interface ScrollState {
  // Scroll state
  isScrolled: boolean;
  scrollProgress: number; // 0 to 1, where 0 = top, 1 = scrolled down
  scrollY: number; // Current scroll position
  
  // Scroll actions
  setIsScrolled: (isScrolled: boolean) => void;
  setScrollProgress: (progress: number) => void;
  setScrollY: (scrollY: number) => void;
  updateScrollState: (scrollY: number) => void;
  
  // Utility actions
  resetScrollState: () => void;
  
  // Configuration
  scrollThreshold: number; // Threshold for isScrolled (default: 100)
  shrinkStart: number; // Start shrinking at this scroll position (default: 50)
  shrinkEnd: number; // Fully shrunk at this scroll position (default: 300)
  
  // Performance optimization
  lastUpdateTime: number;
  throttledUpdateScrollState: (scrollY: number) => void;
}

export const useScrollStore = create<ScrollState>((set, get) => ({
  // Initial state
  isScrolled: false,
  scrollProgress: 0,
  scrollY: 0,
  lastUpdateTime: 0,
  
  // Configuration
  scrollThreshold: 100,
  shrinkStart: 50,
  shrinkEnd: 300,
  
  // Scroll actions
  setIsScrolled: (isScrolled) => set({ isScrolled }),
  setScrollProgress: (scrollProgress) => set({ scrollProgress }),
  setScrollY: (scrollY) => set({ scrollY }),
  
  // Combined scroll update
  updateScrollState: (scrollY) => {
    const { scrollThreshold, shrinkStart, shrinkEnd } = get();
    const isScrolled = scrollY > scrollThreshold;
    
    // Calculate scroll progress (0 to 1)
    const scrollProgress = Math.min(Math.max((scrollY - shrinkStart) / (shrinkEnd - shrinkStart), 0), 1);
    
    set({ isScrolled, scrollProgress, scrollY });
  },
  
  // Throttled scroll update for better performance
  throttledUpdateScrollState: (scrollY) => {
    const now = performance.now();
    const { lastUpdateTime } = get();
    
    // Throttle to ~60fps (16ms intervals)
    if (now - lastUpdateTime >= 16) {
      const { scrollThreshold, shrinkStart, shrinkEnd } = get();
      const isScrolled = scrollY > scrollThreshold;
      
      // Calculate scroll progress (0 to 1)
      const scrollProgress = Math.min(Math.max((scrollY - shrinkStart) / (shrinkEnd - shrinkStart), 0), 1);
      
      set({ 
        isScrolled, 
        scrollProgress, 
        scrollY, 
        lastUpdateTime: now 
      });
    }
  },
  
  // Utility actions
  resetScrollState: () => set({ 
    isScrolled: false, 
    scrollProgress: 0,
    scrollY: 0,
    lastUpdateTime: 0
  }),
}));

// ===== SELECTOR HOOKS =====

// Scroll state selectors - using individual selectors to avoid object creation
export const useScrollState = () => {
  const isScrolled = useScrollStore((state) => state.isScrolled);
  const scrollProgress = useScrollStore((state) => state.scrollProgress);
  const scrollY = useScrollStore((state) => state.scrollY);
  const updateScrollState = useScrollStore((state) => state.updateScrollState);
  const throttledUpdateScrollState = useScrollStore((state) => state.throttledUpdateScrollState);
  const resetScrollState = useScrollStore((state) => state.resetScrollState);
  
  return { isScrolled, scrollProgress, scrollY, updateScrollState, throttledUpdateScrollState, resetScrollState };
};

// Individual scroll state selectors
export const useIsScrolled = () => useScrollStore((state) => state.isScrolled);
export const useScrollProgress = () => useScrollStore((state) => state.scrollProgress);
export const useScrollY = () => useScrollStore((state) => state.scrollY);
export const useUpdateScrollState = () => useScrollStore((state) => state.updateScrollState);
export const useResetScrollState = () => useScrollStore((state) => state.resetScrollState);

// Configuration selectors
export const useScrollConfig = () => {
  const scrollThreshold = useScrollStore((state) => state.scrollThreshold);
  const shrinkStart = useScrollStore((state) => state.shrinkStart);
  const shrinkEnd = useScrollStore((state) => state.shrinkEnd);
  
  return { scrollThreshold, shrinkStart, shrinkEnd };
};
